import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  INTERVIEW_STATUSES,
  INTERVIEW_STATUS_MAP,
  type InterviewStatus,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Notice,
  PageHead,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api, qs } from '../../lib/api';
import { initials, longDay, plural, time, WORDS } from '../../lib/format';

interface AgendaRow {
  id: string;
  kind: string;
  kindLabel: string;
  kindTone: string;
  stageLabel: string;
  scheduledAt: string;
  minutes: number;
  place: string | null;
  meetingUrl: string | null;
  status: InterviewStatus;
  statusLabel: string;
  statusKind: string;
  pending: boolean;
  outcome: string | null;
  applicationId: string;
  candidateId: string;
  name: string;
  phone: string | null;
  job: { id: string; title: string };
}

/**
 * مواعيد المقابلات.
 *
 * قائمةٌ مرتّبةٌ بالزمن لا شبكةُ شهر: من يفتح هذه الشاشة يسأل «ماذا عندي
 * بعد؟» لا «كيف يبدو أكتوبر». وكلُّ موعدٍ هنا له صفٌّ في تقويم الشركة
 * باسم «مقابلات التوظيف» — فمن أراد الشبكة أو التذكير وجدهما هناك.
 */
export function RecruitAgenda() {
  const [scope, setScope] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [status, setStatus] = useState('');

  const now = new Date();
  const range =
    scope === 'upcoming'
      ? { from: new Date(now.getTime() - 60 * 60 * 1000).toISOString() }
      : scope === 'past'
        ? { to: now.toISOString() }
        : {};

  const list = useQuery({
    queryKey: ['recruit', 'agenda', scope, status],
    queryFn: () =>
      api<AgendaRow[]>(
        `/hr/recruit/interviews${qs({ ...range, status: status || undefined })}`,
      ),
  });

  const rows = list.data ?? [];
  const ordered = scope === 'past' ? [...rows].reverse() : rows;

  /* التجميعُ باليوم: الأيّامُ هي ما يفكّر به من ينظّم مواعيده. */
  const days = new Map<string, AgendaRow[]>();
  for (const r of ordered) {
    const key = r.scheduledAt.slice(0, 10);
    const bucket = days.get(key) ?? [];
    bucket.push(r);
    days.set(key, bucket);
  }

  return (
    <div className="page">
      <PageHead
        title="مواعيد المقابلات"
        subtitle={
          list.data ? plural(rows.length, WORDS.record) : undefined
        }
        actions={
          <Link className="btn" to="/crm/calendar">
            <Icon name="calendar" size={15} />
            تقويم الشركة
          </Link>
        }
      />

      <div className="toolbar">
        <select
          className="select"
          value={scope}
          onChange={(e) => setScope(e.target.value as typeof scope)}
        >
          <option value="upcoming">القادمة</option>
          <option value="past">التي مضت</option>
          <option value="all">الكلّ</option>
        </select>

        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {INTERVIEW_STATUSES.map((s) => (
            <option key={s} value={s}>
              {INTERVIEW_STATUS_MAP[s].label}
            </option>
          ))}
        </select>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      <Notice title="التذكير من التقويم لا من هنا" icon="clock">
        كلُّ موعدٍ يُحجز هنا يُكتب صفًّا في تقويم «مقابلات التوظيف»، ومنه
        يخرج التذكير قبل الموعد بالمدّة المضبوطة في إعدادات الإشعارات.
      </Notice>

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title={scope === 'past' ? 'لا مواعيد مضت' : 'لا مواعيد قادمة'}
          text="المواعيد تُحجز من ملفّ الطلب في مسار التوظيف."
          action={
            <Link className="btn btn-primary" to="/hr/recruit/pipeline">
              افتح المسار
            </Link>
          }
        />
      )}

      {[...days.entries()].map(([day, items]) => (
        <div className="card" key={day}>
          <h2 className="card-title">{longDay(items[0]!.scheduledAt)}</h2>

          <div className="rc-agenda">
            {items.map((r) => (
              <Link
                key={r.id}
                to={`/hr/recruit/pipeline?open=${r.applicationId}`}
                className="rc-slot"
              >
                <span className="rc-slot-time num">
                  {time(r.scheduledAt)}
                  <span className="tiny mute">{r.minutes} د</span>
                </span>

                <span className="rc-slot-body">
                  <span className="row" style={{ gap: 'var(--sp-2)' }}>
                    <span className="rc-avatar" aria-hidden="true">
                      {initials(r.name)}
                    </span>
                    <strong>{r.name}</strong>
                    <Badge kind={r.kindTone}>{r.kindLabel}</Badge>
                    {!r.pending && (
                      <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                    )}
                  </span>
                  <span className="tiny mute">
                    {r.job.title} · {r.stageLabel}
                  </span>
                  {r.place && <span className="tiny mute">{r.place}</span>}
                  {r.meetingUrl && (
                    <span className="tiny mute" dir="ltr">
                      {r.meetingUrl}
                    </span>
                  )}
                  {r.outcome && <span className="tiny">{r.outcome}</span>}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
